import { useState } from "react";

import {
  AssetsService,
  type AssetBaseResponse,
  type AssetCreatePayload,
  type AssetType,
  type RiskClassification,
} from "@/api";
import { useAuth } from "@/hooks/use-auth";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { ASSET_TYPES, RISK_CLASSIFICATIONS } from "@/lib/asset-enums";

// Enum values map to translation keys so type/risk labels follow
// the active locale.
const ASSET_TYPE_KEY: Record<AssetType, TranslationKey> = {
  prompt: "assets.types.prompt",
  model: "assets.types.model",
  agent: "assets.types.agent",
  workflow: "assets.types.workflow",
  provider: "assets.types.provider",
  tool: "assets.types.tool",
  dataset: "assets.types.dataset",
};

const RISK_KEY: Record<RiskClassification, TranslationKey> = {
  low: "assets.risk.low",
  medium: "assets.risk.medium",
  high: "assets.risk.high",
  critical: "assets.risk.critical",
};
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";

interface AssetCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (created: AssetBaseResponse) => void;
}

interface MutationInput {
  assetType: AssetType;
  payload: AssetCreatePayload;
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");
}

export function AssetCreateDialog({
  open,
  onClose,
  onSuccess,
}: AssetCreateDialogProps) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [assetType, setAssetType] = useState<AssetType>("prompt");
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugDirty, setSlugDirty] = useState(false);
  const [description, setDescription] = useState("");
  const [risk, setRisk] = useState<RiskClassification>("low");
  const [businessJustification, setBusinessJustification] = useState("");
  const [tagsInput, setTagsInput] = useState("");

  const { mutate, isLoading, error, reset } = useMutation<
    MutationInput,
    AssetBaseResponse
  >(({ assetType: type, payload }) => AssetsService.create(type, payload), {
    onSuccess: (data) => {
      setName("");
      setSlug("");
      setSlugDirty(false);
      setDescription("");
      setBusinessJustification("");
      setTagsInput("");
      onSuccess(data);
    },
  });

  const handleNameChange = (next: string) => {
    setName(next);
    if (!slugDirty) setSlug(slugify(next));
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const canSubmit = name.trim().length > 0 && slug.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const tags = tagsInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    const payload: AssetCreatePayload = {
      name: name.trim(),
      slug,
      risk_classification: risk,
    };
    const trimmedDesc = description.trim();
    if (trimmedDesc) payload.description = trimmedDesc;
    const trimmedBJ = businessJustification.trim();
    if (trimmedBJ) payload.business_justification = trimmedBJ;
    if (tags.length > 0) payload.tags = tags;
    if (user?.workspace_id) payload.workspace_id = user.workspace_id;
    await mutate({ assetType, payload });
  };

  return (
    <Dialog open={open} onClose={handleClose} size="max-w-2xl">
      <DialogHeader>
        <DialogTitle>{t("assets.createDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("assets.createDialog.description")}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          <div>
            <span className="mb-1.5 block text-sm font-medium text-ocean-900">
              {t("assets.createDialog.typeLabel")}
            </span>
            <div className="flex flex-wrap gap-2">
              {ASSET_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setAssetType(type)}
                  aria-pressed={assetType === type}
                  className={
                    "rounded-md border px-3 py-1.5 text-xs font-medium transition-colors " +
                    (assetType === type
                      ? "border-ocean-600 bg-ocean-50 text-ocean-900"
                      : "border-ocean-100 bg-white text-neutral-600 hover:border-ocean-200")
                  }
                >
                  {t(ASSET_TYPE_KEY[type])}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
            <FormField
              label={t("assets.createDialog.nameLabel")}
              value={name}
              onChange={handleNameChange}
              placeholder={t("assets.createDialog.namePlaceholder", {
                type: t(ASSET_TYPE_KEY[assetType]),
              })}
              maxLength={255}
              required
              autoFocus
            />
            <FormField
              label={t("assets.createDialog.slugLabel")}
              value={slug}
              onChange={(v) => {
                setSlugDirty(true);
                setSlug(v);
              }}
              maxLength={100}
              required
              hint={t("assets.createDialog.slugHint")}
            />
          </div>

          <FormField
            label={t("assets.createDialog.descriptionLabel")}
            value={description}
            onChange={setDescription}
            placeholder={t("assets.createDialog.descriptionPlaceholder")}
            maxLength={500}
          />

          <div>
            <span className="mb-1.5 block text-sm font-medium text-ocean-900">
              {t("assets.createDialog.riskLabel")}
            </span>
            <div className="flex flex-wrap gap-2">
              {RISK_CLASSIFICATIONS.map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setRisk(level)}
                  aria-pressed={risk === level}
                  className={
                    "rounded-md border px-3 py-1.5 text-xs font-medium transition-colors " +
                    (risk === level
                      ? "border-ocean-600 bg-ocean-50 text-ocean-900"
                      : "border-ocean-100 bg-white text-neutral-600 hover:border-ocean-200")
                  }
                >
                  {t(RISK_KEY[level])}
                </button>
              ))}
            </div>
          </div>

          <FormField
            label={t("assets.createDialog.justificationLabel")}
            value={businessJustification}
            onChange={setBusinessJustification}
            placeholder={t("assets.createDialog.justificationPlaceholder")}
            maxLength={500}
          />

          <FormField
            label={t("assets.createDialog.tagsLabel")}
            value={tagsInput}
            onChange={setTagsInput}
            placeholder={t("assets.createDialog.tagsPlaceholder")}
            hint={t("assets.createDialog.tagsHint")}
          />

          {error && <Alert variant="danger">{parseApiError(error)}</Alert>}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("assets.createDialog.cancel")}
          </Button>
          <Button type="submit" disabled={isLoading || !canSubmit}>
            {isLoading
              ? t("assets.createDialog.submitLoading")
              : t("assets.createDialog.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
